import React from 'react';
import { capitalCase } from 'change-case';
import { PageHeader, Timeline, Descriptions, Tag, Skeleton } from 'antd';
import { StackedPickup } from '../../types/Pickup';
import { StackedDropoff } from '../../types/Dropoff';
import { DELIVERY_OPERATIONS } from '../../types/Delivery';

interface StackedDeliveryTimelinePropsInterface {
    isLoadingData: boolean;
    pickups: StackedPickup[];
    dropoffs: StackedDropoff[];
}

const StackedDeliveryTimeline: React.FC<StackedDeliveryTimelinePropsInterface> = (
    props: StackedDeliveryTimelinePropsInterface,
): React.ReactElement => {
    const {
        pickups: [pickup],
        dropoffs,
        isLoadingData,
    } = props;

    const deliveryOperations: any[] = [
        { deliveryOpType: DELIVERY_OPERATIONS.PICK_UP, ...pickup },
        ...dropoffs.map((dropoff) => ({ deliveryOpType: DELIVERY_OPERATIONS.DROP_OFF, ...dropoff })),
    ];

    const timeLineFormat: { [key: string]: { color: string } } = {
        [DELIVERY_OPERATIONS.PICK_UP]: {
            color: '#ff9c6e',
        },
        [DELIVERY_OPERATIONS.DROP_OFF]: {
            color: '#4b8b26',
        },
    };

    if (isLoadingData) {
        return <Skeleton active />;
    }

    return (
        <div className="shadow-sm">
            <PageHeader
                className="site-page-header py-2 justify-content-end"
                title={<p className="h5 m-0">Delivery details</p>}
                extra={[
                    <span key="num_deliveries" className="text-secondary">
                        N&deg; drop-off:
                        {dropoffs.length}
                    </span>,
                ]}
            />
            <Timeline className="p-3">
                {deliveryOperations.map((deliveryOp, index) => (
                    <Timeline.Item
                        key={`${deliveryOp.deliveryOpType}_${deliveryOp.id}`}
                        color={timeLineFormat[deliveryOp.deliveryOpType].color}
                    >
                        <Descriptions
                            /* eslint-disable prettier/prettier */
                            title={(
                                <div className="d-flex flex-row justify-content-between">
                                    <p className="m-0">
                                        {deliveryOp.deliveryOpType === DELIVERY_OPERATIONS.DROP_OFF && (<Tag color="#4b8b26">{index}</Tag>)}
                                        <span>{capitalCase(deliveryOp.deliveryOpType)}</span>
                                    </p>
                                    {deliveryOp.deliveryOpType === DELIVERY_OPERATIONS.DROP_OFF && (
                                        <p className="m-0 text-right text-secondary">
                                            <small>
                                                <strong>Client reference:</strong>
                                                <br />
                                                <span className="text-uppercase">{deliveryOp.clientReference}</span>
                                            </small>
                                        </p>
                                    )}
                                </div>
                            )}
                            /* eslint-enable prettier/prettier */
                            layout="vertical"
                            size="small"
                        >
                            <Descriptions.Item label={<strong>Contact</strong>}>
                                <p className="m-0">
                                    <span>Full name: </span>
                                    <span className="text-secondary">
                                        {`${deliveryOp.contact.firstname || '-'} ${deliveryOp.contact.lastname || '-'}`}
                                    </span>
                                    <br />
                                    <span>Phone n&deg;: </span>
                                    <span className="text-secondary">{deliveryOp.contact.phone || '-'}</span>
                                    <br />
                                    <span>E-mail: </span>
                                    {deliveryOp.contact.email ? (
                                        <a
                                            id="link-contact-email"
                                            href={`mailto:${deliveryOp.contact.email}`}
                                            title="E-mail"
                                            target="blank"
                                            className="text-lowercase"
                                        >
                                            {deliveryOp.contact.email}
                                        </a>
                                    ) : (
                                        <span>-</span>
                                    )}
                                    <br />
                                    <span>Company: </span>
                                    <span className="text-secondary">{deliveryOp.contact.company || '-'}</span>
                                </p>
                            </Descriptions.Item>
                            <Descriptions.Item label={<strong>Address</strong>}>
                                <p className="m-0">
                                    <span className="w-100 d-block text-secondary">{deliveryOp.address}</span>
                                    <a
                                        href={`http://www.google.com/maps/place/${deliveryOp.latitude},${deliveryOp.longitude}`}
                                        title="View on Google Maps"
                                        target="blank"
                                    >
                                        View on Google Maps
                                    </a>
                                </p>
                            </Descriptions.Item>
                            {deliveryOp.deliveryOpType === DELIVERY_OPERATIONS.DROP_OFF && (
                                <Descriptions.Item label={<strong>Package details</strong>}>
                                    <p className="m-0">
                                        <span>Type: </span>
                                        <span className="text-secondary">{deliveryOp.packageType}</span>
                                        <br />
                                        <span>Description: </span>
                                        <span className="text-secondary">{deliveryOp.packageDescription! || '-'}</span>
                                    </p>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={<strong>Comment</strong>}>
                                <p className="m-0 text-secondary">{deliveryOp.comment || '-'}</p>
                            </Descriptions.Item>
                        </Descriptions>
                    </Timeline.Item>
                ))}
            </Timeline>
        </div>
    );
};

export default StackedDeliveryTimeline;
